// components/NewRequestModal.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { loadProductSamples, createSampleRequest, listSalesStates } from "../services/posService";
import "./NewRequestModal.css";

// The full 50 stays the fallback and the second group. The tenant's own
// sales_states are surfaced first (see the picker below) but never restrict
// the list — sending a sample into a state you don't sell in yet is often the
// whole point of sending it. (backlog item 61)
const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming",
];

const EMPTY_FORM = {
  shipToCompany:     "",
  address:           "",
  state:             "",
  contactName:       "",
  contactPhone:      "",
  contactEmail:      "",
  requestedShipDate: "",
  notes:             "",
};

export default function NewRequestModal({ user, onClose, onCreated }) {
  const [form,         setForm]         = useState(EMPTY_FORM);
  const [products,     setProducts]     = useState([]);
  const [productQtys,  setProductQtys]  = useState({});
  const [loadingProds, setLoadingProds] = useState(true);
  const [mySalesStates, setMySalesStates] = useState([]);
  const [submitting,   setSubmitting]   = useState(false);
  const [errors,       setErrors]       = useState({});

  useEffect(() => {
    loadProductSamples()
      .then(setProducts)
      .catch(err => console.error("Could not load product samples:", err))
      .finally(() => setLoadingProds(false));
  }, []);

  // Non-blocking: if this fails the picker just shows the flat 50-state list.
  useEffect(() => {
    listSalesStates()
      .then(rows => {
        const names = [...new Set(
          (rows || []).filter(r => r.active !== false).map(r => r.state_name).filter(Boolean)
        )].filter(n => US_STATES.includes(n)).sort();
        setMySalesStates(names);
      })
      .catch(err => console.error("[NewRequestModal] could not load sales states:", err));
  }, []);

  const otherStates = mySalesStates.length
    ? US_STATES.filter(s => !mySalesStates.includes(s))
    : US_STATES;

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const setProductQty = (name, qty) => {
    setProductQtys(prev => ({ ...prev, [name]: qty }));
    if (errors.products) setErrors(prev => ({ ...prev, products: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.shipToCompany.trim()) e.shipToCompany  = "Required";
    if (!form.address.trim())       e.address        = "Required";
    if (!form.state)                e.state          = "Required";
    if (!form.contactName.trim())   e.contactName    = "Required";
    if (!form.contactPhone.trim())  e.contactPhone   = "Required";
    if (!form.contactEmail.trim())  e.contactEmail   = "Required";
    if (!form.requestedShipDate)    e.requestedShipDate = "Required";
    const hasProduct = Object.values(productQtys).some(q => parseInt(q, 10) >= 1);
    if (!hasProduct)                e.products       = "Enter a quantity for at least one product";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    try {
      const productStr = Object.entries(productQtys)
        .filter(([, q]) => parseInt(q, 10) >= 1)
        .map(([name, q]) => `${name} (x${parseInt(q, 10)})`)
        .join(", ");
      const today = new Date().toISOString().split("T")[0];

      const record = await createSampleRequest({
        submitter:         user?.displayName || user?.mail || "",
        submitterEmail:    user?.mail        || "",
        shipToCompany:     form.shipToCompany.trim(),
        address:           form.address.trim(),
        state:             form.state,
        contactName:       form.contactName.trim(),
        contactPhone:      form.contactPhone.trim(),
        contactEmail:      form.contactEmail.trim(),
        orderDate:         today,
        requestedShipDate: form.requestedShipDate,
        product:           productStr,
        quantity:          "",
        notes:             form.notes.trim(),
        status:            "Not Shipped",
      });

      onCreated(record);
      onClose();
    } catch (err) {
      console.error("Create error:", err);
      setErrors({ submit: err.message });
    } finally {
      setSubmitting(false);
    }
  };


  // No backdrop click-to-close — users were losing in-progress request data
  // when they accidentally clicked outside the modal. Close via the X / Cancel
  // buttons only.
  // Portaled to <body> so the fixed overlay escapes the app shell's stacking
  // context and renders above the sticky topnav (which otherwise paints over
  // the modal's header — see NewPOSModal for the same fix).
  return createPortal(
    // `.pos-module` on the overlay re-supplies the the source app→BC CSS-var bridge once
    // portaled to <body>; without it the modal panel background is transparent.
    <div className="nrm-overlay pos-module">
      <div className="nrm-modal">

        {/* Header */}
        <div className="nrm-header">
          <div className="nrm-title">New Sample Request</div>
          <button className="nrm-close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="nrm-body">

          {/* Ship To */}
          <div className="nrm-section-title">Ship To</div>
          <div className="nrm-row">
            <div className={`nrm-field nrm-field-grow${errors.shipToCompany ? " nrm-field-error" : ""}`}>
              <label>Company <span className="nrm-req">*</span></label>
              <input value={form.shipToCompany} onChange={e => set("shipToCompany", e.target.value)} placeholder="Company name" />
              {errors.shipToCompany && <div className="nrm-err-msg">{errors.shipToCompany}</div>}
            </div>
            <div className={`nrm-field nrm-field-state${errors.state ? " nrm-field-error" : ""}`}>
              <label>State <span className="nrm-req">*</span></label>
              <select value={form.state} onChange={e => set("state", e.target.value)}>
                <option value="">— Select —</option>
                {mySalesStates.length > 0 && (
                  <optgroup label="Your sales states">
                    {mySalesStates.map(s => <option key={s} value={s}>{s}</option>)}
                  </optgroup>
                )}
                {mySalesStates.length > 0 ? (
                  <optgroup label="All other states">
                    {otherStates.map(s => <option key={s} value={s}>{s}</option>)}
                  </optgroup>
                ) : (
                  otherStates.map(s => <option key={s} value={s}>{s}</option>)
                )}
              </select>
              {errors.state && <div className="nrm-err-msg">{errors.state}</div>}
            </div>
          </div>
          <div className={`nrm-field${errors.address ? " nrm-field-error" : ""}`}>
            <label>Address <span className="nrm-req">*</span></label>
            <input value={form.address} onChange={e => set("address", e.target.value)} placeholder="Street, City, State ZIP" />
            {errors.address && <div className="nrm-err-msg">{errors.address}</div>}
          </div>

          {/* Contact */}
          <div className="nrm-section-title">Contact</div>
          <div className="nrm-row">
            <div className={`nrm-field nrm-field-grow${errors.contactName ? " nrm-field-error" : ""}`}>
              <label>Name <span className="nrm-req">*</span></label>
              <input value={form.contactName} onChange={e => set("contactName", e.target.value)} placeholder="Contact name" />
              {errors.contactName && <div className="nrm-err-msg">{errors.contactName}</div>}
            </div>
            <div className={`nrm-field${errors.contactPhone ? " nrm-field-error" : ""}`}>
              <label>Phone <span className="nrm-req">*</span></label>
              <input value={form.contactPhone} onChange={e => set("contactPhone", e.target.value)} placeholder="Phone number" />
              {errors.contactPhone && <div className="nrm-err-msg">{errors.contactPhone}</div>}
            </div>
            <div className={`nrm-field nrm-field-grow${errors.contactEmail ? " nrm-field-error" : ""}`}>
              <label>Email <span className="nrm-req">*</span></label>
              <input value={form.contactEmail} onChange={e => set("contactEmail", e.target.value)} placeholder="email@example.com" type="email" />
              {errors.contactEmail && <div className="nrm-err-msg">{errors.contactEmail}</div>}
            </div>
          </div>

          {/* Products */}
          <div className="nrm-section-title">
            Products <span className="nrm-req">*</span>
            {errors.products && <span className="nrm-err-inline">{errors.products}</span>}
          </div>
          {loadingProds ? (
            <div className="nrm-loading">Loading products…</div>
          ) : (
            <div className="nrm-products">
              <div className="nrm-product-qty-list">
                {products.map(p => (
                  <div key={p.id} className="nrm-product-qty-row">
                    <div className="nrm-product-qty-name">
                      {p.name}
                      {p.description && <span className="nrm-product-desc"> — {p.description}</span>}
                    </div>
                    <input
                      type="number"
                      min="0"
                      className="nrm-product-qty-input"
                      value={productQtys[p.name] || ""}
                      onChange={e => setProductQty(p.name, e.target.value)}
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Details */}
          <div className="nrm-section-title">Request Details</div>
          <div className="nrm-row">
            <div className={`nrm-field${errors.requestedShipDate ? " nrm-field-error" : ""}`}>
              <label>Requested Ship Date <span className="nrm-req">*</span></label>
              <input type="date" value={form.requestedShipDate} onChange={e => set("requestedShipDate", e.target.value)} />
              {errors.requestedShipDate && <div className="nrm-err-msg">{errors.requestedShipDate}</div>}
            </div>
          </div>
          <div className="nrm-field">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Any additional notes…" rows={3} />
          </div>

          {errors.submit && <div className="nrm-submit-error">{errors.submit}</div>}
        </div>

        {/* Footer */}
        <div className="nrm-footer">
          <button className="nrm-cancel" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="nrm-submit" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Request"}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
}
